import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';

import { ControleVoorraadClient, ControleVoorraad, KleurStatusEnum } from '../client/procescontroleclient';
import Table from "../components/tables/Table";
import Button from "../components/UI/Button";
import Refresh from "../components/UI/Refresh";
import PageTitle from '../components/UI/PageTitle';
import ButtonWrap, { MobileButtonWrap } from '../components/UI/ButtonWrap';
import Input from '../components/UI/Input';
import ProcesControleMobileCard, { Row, Item, Label, Field } from "../components/UI/ProcesControleMobileCard";
import { ButtonType, TableType, InputType } from "../Types";
import Spinner from "../components/UI/Spinner";
import useDebounce from "../hooks/useDebounce";
import { getDateTimeFormat } from "../utilities/Utilities";
import PageWrapper from '../components/UI/PageWrapper';

interface ControleVoorraadProps {
  bloemOfPlanten: number;
}
  
interface Props {
  color: KleurStatusEnum;
}

const Tr = styled.tr<Props>`
  ${props => props.color===KleurStatusEnum.Red ? 'background-color: rgba(235,100,96,0.5)!important;' : null};
  ${props => props.color===KleurStatusEnum.DarkRed ? 'background-color: rgba(234,100,96,0.8)!important;' : null}; 
`;

export default function ProcesControleVoorraad(props: ControleVoorraadProps) {
    const voorraadClient = new ControleVoorraadClient(process.env.REACT_APP_APIURL);
    const [filter, setFilter] = useState<string>('');
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
    const queryName: string = props.bloemOfPlanten === 0 ? 'procescontrolevoorraadbloemen' : 'procescontrolevoorraadplanten';
    const debouncedFilter = useDebounce(filter, 300);

    const voorraadQuery = useQuery([queryName], async () => {
      if(props.bloemOfPlanten === 0) {
        return await voorraadClient.getBloemen();
      } else {
        return await voorraadClient.getPlanten();
      }
    }, {
        staleTime: Infinity
    });

    const checkFilterRow: any = (filterRow: ControleVoorraad) => {

      if (debouncedFilter === '') return filterRow;
  
      return (filterRow.omschrijving.toLowerCase().includes(filter.toLowerCase()) || 
              filterRow.vPartijnummer.toString().includes(filter.toLowerCase())
              );
    }

    const voorraadData = useMemo(() => {

      if (voorraadQuery.data) {
        return voorraadQuery.data.filter(checkFilterRow);
      }
      return [];
    }, [voorraadQuery.data, debouncedFilter]);

if (voorraadQuery.isLoading || voorraadQuery.isFetching) {
    return <Spinner />;
}

return (
    <PageWrapper>
      <PageTitle>{props.bloemOfPlanten === 0 ? 'Voorraad bloemen controle' : 'Voorraad planten controle'}</PageTitle>
      <ButtonWrap>
        {!isMobile ? <Button buttonType={ButtonType.Primary} spacingR svg onClick={() => voorraadQuery.refetch()}><FontAwesomeIcon icon="eye"/>Opvragen</Button>
        : <Refresh onClick={() => voorraadQuery.refetch()} />}
          <MobileButtonWrap>
            <Input
              inputType={InputType.SearchInput}  
              onChange={(e: React.FormEvent<HTMLInputElement>) => setFilter(e.currentTarget.value)}
              value={filter}
            />
          </MobileButtonWrap>
      </ButtonWrap>
      {!isMobile ?
      <Table tableType={TableType.Default} stickymodal>
        <thead>
          <tr>
              <th style={{textAlign: 'left', width : "5%"}} scope="col">Vpartijnummer</th>
              <th style={{textAlign: 'left', width : "20%"}} scope="col">Omschrijving</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Kolli</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Inh kolli</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Los</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Stuks</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Lengte</th>
              <th style={{textAlign: 'right', width : "5%"}} scope="col">Klantverdeling</th>
              <th style={{textAlign: 'left', width : "5%"}} scope="col">Binnentijd Kubus</th>
          </tr>
        </thead>
        <tbody>
        {voorraadData.map((row, index) => {
            return (
              <Tr key={index} color={row.kleurStatus}>
                  <td>{row.vPartijnummer}</td>
                  <td>{row.omschrijving}</td>
                  <td style={{textAlign: 'right'}}>{row.kolli}</td>
                  <td style={{textAlign: 'right'}}>{row.kolliInhoud}</td>
                  <td style={{textAlign: 'right'}}>{row.los}</td>
                  <td style={{textAlign: 'right'}}>{row.stuks}</td>
                  <td style={{textAlign: 'right'}}>{row.lengte}</td>
                  <td style={{textAlign: 'right'}}>{row.customerOrderAmount}</td>
                  <td>{getDateTimeFormat(row.binnenTijdKubus)}</td>
              </Tr>
            )
        })}
        </tbody>
      </Table> :
      voorraadData?.map((row, index) => (
        <ProcesControleMobileCard key={index} color={row.kleurStatus}
          compactView={
              <div><strong>Omschr.</strong>{row.omschrijving.substring(0, 40)}{row.omschrijving.length >= 40 && '..'}</div>
          }
          expandView={
            <>
              <Row>
                <Item><Label>Omschrijving</Label><Field>{row.omschrijving}</Field></Item>
              </Row>
              <Row>
                <Item>
                  <Label>Kolli</Label><Field>{row.kolli}</Field>
                </Item>
                <Item>
                  <Label>Inhoud</Label><Field>{row.kolliInhoud}</Field>
                </Item>
              </Row>
              <Row>
                <Item>
                  <Label>Los</Label><Field>{row.los}</Field>
                </Item>
                <Item>
                  <Label>Stuks</Label><Field>{row.stuks}</Field>
                </Item>                
              </Row>
              <Row>
                <Item>
                  <Label>Lengte</Label><Field>{row.lengte}</Field>
                </Item>
                <Item>
                  <Label>Binnentijd kubus</Label><Field>{getDateTimeFormat(row.binnenTijdKubus)}</Field>
                </Item>
              </Row>
              <Row>
                <Item>
                  <Label>Klantverdeling</Label><Field>{row.customerOrderAmount}</Field>
                </Item>
              </Row>
            </>
          } 
          checked={undefined}           
          title={row.vPartijnummer.toString()} />
      ))}
    </PageWrapper>
  );
}